import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { settingsApi, leadFormApi, invoiceApi } from '../lib/api';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { User, Building2, Save, Link2, Unlink, Mail, Calendar, Bell, FileText, Copy, Check, ExternalLink, RefreshCw, Moon, Sun, Palette, Upload, Trash2, Crown, Image, Lock, BellRing, CreditCard, Receipt, Calculator, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { APP_VERSION } from '../data/releaseNotes';
import { StripePaymentsSettings } from '../components/StripePaymentsSettings';

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, loading: pushLoading, subscribe: subscribePush, unsubscribe: unsubscribePush } = usePushNotifications();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsData, setSettingsData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
  });
  const [userSettings, setUserSettings] = useState({
    email_notifications: true,
    reminder_days_before: 1,
    auto_followup_new_lead_hours: 24,
    auto_followup_estimate_days: 7,
  });
  const [leadFormData, setLeadFormData] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  // Show a "new" dot next to What's New if the latest version hasn't been viewed
  const hasNewUpdate = (() => {
    try {
      return localStorage.getItem('corbel_last_seen_version') !== APP_VERSION;
    } catch {
      return false;
    }
  })();
  
  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Branding state
  const [brandingData, setBrandingData] = useState(null);
  const [selectedScheme, setSelectedScheme] = useState('default');
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef(null);

  // Invoice settings state
  const [invoiceSettings, setInvoiceSettings] = useState({
    company_address: '',
    company_city: '',
    company_state: '',
    company_zip: '',
    company_phone: '',
    default_tax_rate: 0,
    stripe_payment_link: '',
    venmo_username: '',
    paypal_link: '',
    zelle_info: '',
    include_qr_code: true,
    email_copy_to_self: false,
  });
  const [savingInvoiceSettings, setSavingInvoiceSettings] = useState(false);
  const [estimateDefaults, setEstimateDefaults] = useState({
    labor_rate: 42, material_tax_pct: 6.85, cya_pct: 5, overhead_pct: 10,
  });
  const [canEditEstimateDefaults, setCanEditEstimateDefaults] = useState(false);
  const [savingEstimateDefaults, setSavingEstimateDefaults] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchLeadFormSettings();
    fetchBrandingSettings();
    fetchInvoiceSettings();
    fetchEstimateDefaults();
    // Check for Google OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_connected') === 'true') {
      toast.success('Google account connected successfully!');
      window.history.replaceState({}, '', '/settings');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoiceSettings = async () => {
    try {
      const response = await invoiceApi.getSettings();
      if (response.data) {
        setInvoiceSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.log('Invoice settings not available');
    }
  };

  const handleInvoiceSettingsChange = (field, value) => {
    setInvoiceSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveInvoiceSettings = async () => {
    setSavingInvoiceSettings(true);
    try {
      await invoiceApi.updateSettings(invoiceSettings);
      toast.success('Invoice settings saved');
    } catch (error) {
      toast.error('Failed to save invoice settings');
    } finally {
      setSavingInvoiceSettings(false);
    }
  };

  const fetchEstimateDefaults = async () => {
    try {
      const res = await settingsApi.getEstimateDefaults();
      if (res.data?.defaults) setEstimateDefaults(res.data.defaults);
      setCanEditEstimateDefaults(!!res.data?.can_edit);
    } catch (error) {
      console.log('Estimate defaults not available');
    }
  };

  const handleEstimateDefaultsChange = (field, value) => {
    setEstimateDefaults(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEstimateDefaults = async () => {
    setSavingEstimateDefaults(true);
    try {
      await settingsApi.updateEstimateDefaults(estimateDefaults);
      toast.success('Estimate defaults saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save estimate defaults');
    } finally {
      setSavingEstimateDefaults(false);
    }
  };

  const fetchBrandingSettings = async () => {
    try {
      const response = await settingsApi.getBranding();
      setBrandingData(response.data);
      setSelectedScheme(response.data.color_scheme || 'default');
    } catch (error) {
      console.log('Branding settings not available');
    }
  };

  const fetchLeadFormSettings = async () => {
    try {
      const response = await leadFormApi.getSettings();
      setLeadFormData(response.data);
    } catch (error) {
      // Silently fail - not critical
      console.log('Lead form settings not available');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsApi.get();
      setSettingsData(response.data);
      setFormData({
        name: response.data.user.name || '',
        company: response.data.user.company || '',
      });
      setUserSettings(response.data.settings);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsApi.updateProfile(formData);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingsChange = async (key, value) => {
    const newSettings = { ...userSettings, [key]: value };
    setUserSettings(newSettings);
    try {
      await settingsApi.update(newSettings);
      toast.success('Settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
      setUserSettings(userSettings); // Revert
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await settingsApi.getGoogleAuthUrl();
      window.location.href = response.data.authorization_url;
    } catch (error) {
      if (error.response?.data?.detail?.includes('not configured')) {
        toast.error('Google OAuth is not configured. Please add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to your environment.');
      } else {
        toast.error('Failed to initiate Google connection');
      }
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Disconnect Google account? Calendar sync will stop.')) return;
    try {
      await settingsApi.disconnectGoogle();
      fetchSettings();
      toast.success('Google account disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Google');
    }
  };

  const [testingGoogle, setTestingGoogle] = useState(false);
  const [googleTestResult, setGoogleTestResult] = useState(null);

  const handleTestGoogleCalendar = async () => {
    setTestingGoogle(true);
    setGoogleTestResult(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/google/test-calendar`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const result = await response.json();
      setGoogleTestResult(result);
      if (result.success) {
        toast.success('Google Calendar is working correctly!');
      } else {
        toast.error(result.error || 'Calendar test failed');
      }
    } catch (error) {
      toast.error('Failed to test Google connection');
      setGoogleTestResult({ success: false, error: 'Connection failed' });
    } finally {
      setTestingGoogle(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await settingsApi.changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    const setCopied = type === 'url' ? setCopiedUrl : setCopiedEmbed;
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success('Copied to clipboard!');
      } else {
        throw new Error('Clipboard API not available');
      }
    } catch (err) {
      // Fallback
      try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          toast.success('Copied to clipboard!');
        } else {
          toast.error('Failed to copy - please copy manually');
        }
      } catch (fallbackErr) {
        toast.error('Failed to copy - please copy manually');
      }
    }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('Regenerate form token? The old URL will stop working.')) return;
    setRegenerating(true);
    try {
      const response = await leadFormApi.regenerateToken();
      setLeadFormData(prev => ({ ...prev, ...response.data }));
      toast.success('Form URL regenerated');
    } catch (error) {
      toast.error('Failed to regenerate token');
    } finally {
      setRegenerating(false);
    }
  };

  // Branding handlers
  const handleColorSchemeChange = async (scheme) => {
    if (!brandingData?.has_branding_access) return;
    
    setSelectedScheme(scheme);
    setSavingBranding(true);
    try {
      await settingsApi.updateBranding({ color_scheme: scheme });
      await refreshUser(); // Refresh user to apply new scheme
      toast.success('Color scheme updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update color scheme');
      setSelectedScheme(brandingData.color_scheme || 'default');
    } finally {
      setSavingBranding(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (500KB)
    if (file.size > 500 * 1024) {
      toast.error('Logo must be under 500KB');
      return;
    }

    setUploadingLogo(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await settingsApi.updateBranding({ custom_logo: reader.result });
          await refreshUser();
          fetchBrandingSettings();
          toast.success('Logo uploaded!');
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
          setUploadingLogo(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to process image');
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Remove your custom logo?')) return;
    
    setSavingBranding(true);
    try {
      await settingsApi.deleteLogo();
      await refreshUser();
      fetchBrandingSettings();
      toast.success('Logo removed');
    } catch (error) {
      toast.error('Failed to remove logo');
    } finally {
      setSavingBranding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your account, integrations, and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Settings */}
        <div className="lg:col-span-2">
          <Accordion type="multiple" defaultValue={["profile"]} className="space-y-3">

          {/* Profile Settings */}
          <AccordionItem value="profile" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-profile-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <User className="h-5 w-5" strokeWidth={1.5} />
                Profile Information
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <form onSubmit={handleProfileSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleProfileChange}
                      className="rounded-sm mt-1"
                      data-testid="settings-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={settingsData?.user?.email || ''}
                      disabled
                      className="rounded-sm mt-1 bg-secondary"
                    />
                  </div>
                  {/* Only show company name for owners */}
                  {settingsData?.user?.role === 'owner' && (
                    <div className="sm:col-span-2">
                      <Label htmlFor="company">Company Name</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleProfileChange}
                        placeholder="Your Construction Company LLC"
                        className="rounded-sm mt-1"
                        data-testid="settings-company-input"
                      />
                    </div>
                  )}
                </div>
                <Button type="submit" disabled={saving} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="save-profile-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </AccordionContent>
          </AccordionItem>

          {/* Change Password */}
          {settingsData?.user?.auth_provider !== 'google' && (
            <AccordionItem value="password" className="border rounded-sm px-4">
              <AccordionTrigger className="hover:no-underline" data-testid="settings-password-trigger">
                <span className="flex items-center gap-2 text-base font-semibold">
                  <Lock className="h-5 w-5" strokeWidth={1.5} />
                  Change Password
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <form onSubmit={handleChangePassword} className="space-y-4">
                  <div>
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="rounded-sm mt-1"
                      required
                      data-testid="current-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPassword">New Password</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="rounded-sm mt-1"
                      required
                      minLength={6}
                      data-testid="new-password"
                    />
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword">Confirm New Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="rounded-sm mt-1"
                      required
                      data-testid="confirm-password"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    disabled={changingPassword}
                    className="rounded-sm bg-slate-900 hover:bg-slate-800"
                    data-testid="change-password-btn"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    {changingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                </form>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Appearance Settings */}
          <AccordionItem value="appearance" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-appearance-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <Palette className="h-5 w-5" strokeWidth={1.5} />
                Appearance
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {darkMode ? (
                    <Moon className="h-5 w-5 text-slate-400" />
                  ) : (
                    <Sun className="h-5 w-5 text-amber-500" />
                  )}
                  <div>
                    <p className="font-medium">Dark Mode</p>
                    <p className="text-sm text-muted-foreground">
                      {darkMode ? 'Using dark theme' : 'Using light theme'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={darkMode}
                  onCheckedChange={toggleDarkMode}
                  data-testid="dark-mode-toggle"
                />
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Branding (Pro Feature) - Only for Owners */}
          {(settingsData?.user?.role === 'owner' || !settingsData?.user?.role) && (
          <AccordionItem value="branding" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-branding-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <Crown className="h-5 w-5" strokeWidth={1.5} />
                Branding
                {brandingData?.has_branding_access ? (
                  <Badge className="bg-amber-600 ml-2">Pro</Badge>
                ) : (
                  <Badge variant="outline" className="ml-2">Pro Required</Badge>
                )}
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-6">
              {!brandingData?.has_branding_access ? (
                <div className="p-4 bg-secondary/30 rounded-sm text-center">
                  <Crown className="h-8 w-8 mx-auto mb-2 text-amber-500" />
                  <p className="font-medium">Upgrade to Pro</p>
                  <p className="text-sm text-muted-foreground mb-3">
                    Unlock custom color schemes and logo upload with a Pro subscription
                  </p>
                  <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={() => window.location.href = '/billing'}
                  >
                    View Plans
                  </Button>
                </div>
              ) : (
                <>
                  {/* Color Scheme */}
                  <div>
                    <Label className="mb-3 block">Color Scheme</Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {brandingData?.available_schemes && Object.entries(brandingData.available_schemes).map(([key, scheme]) => (
                        <button
                          key={key}
                          onClick={() => handleColorSchemeChange(key)}
                          disabled={savingBranding}
                          className={`p-3 rounded-sm border-2 transition-all ${
                            selectedScheme === key 
                              ? 'border-slate-900 dark:border-white ring-2 ring-offset-2 ring-slate-900 dark:ring-white' 
                              : 'border-border hover:border-slate-400'
                          }`}
                          data-testid={`color-scheme-${key}`}
                        >
                          <div className="flex gap-1 mb-2 justify-center">
                            <div 
                              className="w-6 h-6 rounded-full" 
                              style={{ backgroundColor: scheme.primary }}
                            />
                            <div 
                              className="w-6 h-6 rounded-full" 
                              style={{ backgroundColor: scheme.secondary }}
                            />
                          </div>
                          <p className="text-xs font-medium text-center">{scheme.name}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  {/* Logo Upload */}
                  <div>
                    <Label className="mb-3 block">Company Logo</Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      Upload your logo to display in the sidebar and on client portals. Max 500KB, recommended size 200x60px.
                    </p>
                    
                    {brandingData?.custom_logo ? (
                      <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-sm">
                        <img 
                          src={brandingData.custom_logo} 
                          alt="Company logo" 
                          className="h-12 max-w-[160px] object-contain"
                        />
                        <div className="flex-1" />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="rounded-sm"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Replace
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteLogo}
                          disabled={savingBranding}
                          className="rounded-sm text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div 
                        className="border-2 border-dashed rounded-sm p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
                        onClick={() => logoInputRef.current?.click()}
                      >
                        <Image className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
                        <p className="font-medium">Click to upload logo</p>
                        <p className="text-sm text-muted-foreground">PNG, JPG, or SVG up to 500KB</p>
                      </div>
                    )}
                    
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      data-testid="logo-upload-input"
                    />
                    
                    {uploadingLogo && (
                      <p className="text-sm text-muted-foreground mt-2">Uploading...</p>
                    )}
                  </div>

                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm dark:bg-blue-950 dark:border-blue-800">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      <strong>Tip:</strong> Your logo and color scheme will appear in the app sidebar and on client project portals.
                    </p>
                  </div>
                </>
              )}
            </AccordionContent>
          </AccordionItem>
          )}

          {/* Integrations */}
          <AccordionItem value="integrations" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-integrations-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <Link2 className="h-5 w-5" strokeWidth={1.5} />
                Integrations
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-6">
              {/* Google Integration */}
              <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-sm">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 bg-white rounded-sm flex items-center justify-center border">
                    <svg viewBox="0 0 24 24" className="h-6 w-6">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-medium">Google</p>
                    <p className="text-sm text-muted-foreground">Calendar sync & Email notifications</p>
                  </div>
                </div>
                {settingsData?.user?.google_connected ? (
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <Badge className="bg-green-700">Connected</Badge>
                      {settingsData?.user?.google_email && (
                        <p className="text-xs text-muted-foreground mt-1">{settingsData.user.google_email}</p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestGoogleCalendar}
                      disabled={testingGoogle}
                      className="rounded-sm"
                      data-testid="test-google-btn"
                    >
                      {testingGoogle ? 'Testing...' : 'Test Calendar'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDisconnectGoogle}
                      className="rounded-sm"
                      data-testid="disconnect-google-btn"
                    >
                      <Unlink className="h-4 w-4 mr-1" />
                      Disconnect
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={handleConnectGoogle}
                    className="rounded-sm"
                    data-testid="connect-google-btn"
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Connect
                  </Button>
                )}
              </div>

              {/* Google Test Result */}
              {googleTestResult && (
                <div className={`p-3 rounded-sm text-sm ${googleTestResult.success ? 'bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800'}`}>
                  {googleTestResult.success ? (
                    <div>
                      <p className="font-medium text-green-800 dark:text-green-200">✓ Google Calendar Connected Successfully!</p>
                      <p className="text-green-700 dark:text-green-300 mt-1">
                        Found {googleTestResult.calendars_count} calendar(s). 
                        Events you create will sync to your Google Calendar.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium text-red-800 dark:text-red-200">✗ Calendar Connection Issue</p>
                      <p className="text-red-700 dark:text-red-300 mt-1">{googleTestResult.error}</p>
                      {googleTestResult.action && (
                        <p className="text-red-600 dark:text-red-400 mt-2 font-medium">Action: {googleTestResult.action}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Note: Google integration requires OAuth credentials. If not configured, contact your administrator to add GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET to the environment.
              </p>
            </AccordionContent>
          </AccordionItem>

          {/* Online Payments (Stripe Connect) */}
          <AccordionItem value="payments" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-payments-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <CreditCard className="h-5 w-5" strokeWidth={1.5} />
                Online Payments
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-6">
              <StripePaymentsSettings />
            </AccordionContent>
          </AccordionItem>

          {/* Automation Settings */}
          <AccordionItem value="automation" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-automation-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <Bell className="h-5 w-5" strokeWidth={1.5} />
                Automation & Notifications
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">Receive email updates for project changes</p>
                </div>
                <Switch
                  checked={userSettings.email_notifications}
                  onCheckedChange={(checked) => handleSettingsChange('email_notifications', checked)}
                  data-testid="email-notifications-switch"
                />
              </div>

              {pushSupported && (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <BellRing className="h-4 w-4" />
                      Browser Push Notifications
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Get instant alerts for new leads (even when browser is minimized)
                    </p>
                  </div>
                  <Switch
                    checked={pushSubscribed}
                    disabled={pushLoading}
                    onCheckedChange={(checked) => checked ? subscribePush() : unsubscribePush()}
                    data-testid="push-notifications-switch"
                  />
                </div>
              )}

              <Separator />

              <div>
                <Label>Auto Follow-up: New Leads</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Create reminder this many hours after a new lead is created
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="168"
                    value={userSettings.auto_followup_new_lead_hours || 24}
                    onChange={(e) => handleSettingsChange('auto_followup_new_lead_hours', parseInt(e.target.value))}
                    className="w-20 rounded-sm"
                  />
                  <span className="text-sm text-muted-foreground">hours</span>
                </div>
              </div>

              <div>
                <Label>Auto Follow-up: After Estimate</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Create reminder this many days after lead moves to Estimate stage
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="30"
                    value={userSettings.auto_followup_estimate_days}
                    onChange={(e) => handleSettingsChange('auto_followup_estimate_days', parseInt(e.target.value))}
                    className="w-20 rounded-sm"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </div>

              <div>
                <Label>Reminder Lead Time</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Days before an event to show reminders
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="7"
                    value={userSettings.reminder_days_before}
                    onChange={(e) => handleSettingsChange('reminder_days_before', parseInt(e.target.value))}
                    className="w-20 rounded-sm"
                  />
                  <span className="text-sm text-muted-foreground">days</span>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Estimating Tools */}
          <AccordionItem value="estimating" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-estimating-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <Calculator className="h-5 w-5" strokeWidth={1.5} />
                Estimating Tools
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Craftsman Estimates</p>
                  <p className="text-sm text-muted-foreground">Enable importing Excel files from Craftsman National Estimator</p>
                </div>
                <Switch
                  checked={userSettings.craftsman_estimates_enabled !== false}
                  onCheckedChange={(checked) => handleSettingsChange('craftsman_estimates_enabled', checked)}
                  data-testid="craftsman-estimates-switch"
                />
              </div>

              <div className="border-t pt-4">
                <p className="font-medium">Estimate Buildup Rates</p>
                <p className="text-sm text-muted-foreground mb-3">
                  Applied when building a manual estimate. {canEditEstimateDefaults
                    ? 'These are saved as your company defaults.'
                    : 'Only owners/managers can change these.'}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="labor_rate">Default Labor Rate ($/hr)</Label>
                    <Input
                      id="labor_rate" type="number" step="0.01" min="0"
                      value={estimateDefaults.labor_rate}
                      onChange={(e) => handleEstimateDefaultsChange('labor_rate', parseFloat(e.target.value) || 0)}
                      disabled={!canEditEstimateDefaults}
                      className="rounded-sm mt-1" data-testid="est-labor-rate"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Prefills each estimate; adjustable per job.</p>
                  </div>
                  <div>
                    <Label htmlFor="material_tax_pct">Material Sales Tax (%)</Label>
                    <Input
                      id="material_tax_pct" type="number" step="0.01" min="0"
                      value={estimateDefaults.material_tax_pct}
                      onChange={(e) => handleEstimateDefaultsChange('material_tax_pct', parseFloat(e.target.value) || 0)}
                      disabled={!canEditEstimateDefaults}
                      className="rounded-sm mt-1" data-testid="est-tax-pct"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Recoups tax paid on materials (applied to material only).</p>
                  </div>
                  <div>
                    <Label htmlFor="cya_pct">Contingency / CYA (%)</Label>
                    <Input
                      id="cya_pct" type="number" step="0.01" min="0"
                      value={estimateDefaults.cya_pct}
                      onChange={(e) => handleEstimateDefaultsChange('cya_pct', parseFloat(e.target.value) || 0)}
                      disabled={!canEditEstimateDefaults}
                      className="rounded-sm mt-1" data-testid="est-cya-pct"
                    />
                  </div>
                  <div>
                    <Label htmlFor="overhead_pct">Overhead (%)</Label>
                    <Input
                      id="overhead_pct" type="number" step="0.01" min="0"
                      value={estimateDefaults.overhead_pct}
                      onChange={(e) => handleEstimateDefaultsChange('overhead_pct', parseFloat(e.target.value) || 0)}
                      disabled={!canEditEstimateDefaults}
                      className="rounded-sm mt-1" data-testid="est-overhead-pct"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Adjust monthly as billing cycles change.</p>
                  </div>
                </div>
                {canEditEstimateDefaults && (
                  <Button
                    onClick={handleSaveEstimateDefaults} disabled={savingEstimateDefaults}
                    className="mt-4 rounded-sm bg-orange-500 hover:bg-orange-600"
                    data-testid="save-estimate-defaults-btn"
                  >
                    {savingEstimateDefaults ? 'Saving...' : 'Save Estimate Defaults'}
                  </Button>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Invoice & Payment Settings */}
          <AccordionItem value="invoices" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-invoices-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                <Receipt className="h-5 w-5" strokeWidth={1.5} />
                Invoice & Payment Settings
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-6">
              {/* Company Address for Invoices */}
              <div>
                <Label className="text-base font-medium">Company Address</Label>
                <p className="text-sm text-muted-foreground mb-3">This address will appear on your invoices</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="company_address">Street Address</Label>
                    <Input
                      id="company_address"
                      value={invoiceSettings.company_address}
                      onChange={(e) => handleInvoiceSettingsChange('company_address', e.target.value)}
                      placeholder="123 Main Street"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_city">City</Label>
                    <Input
                      id="company_city"
                      value={invoiceSettings.company_city}
                      onChange={(e) => handleInvoiceSettingsChange('company_city', e.target.value)}
                      placeholder="Austin"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label htmlFor="company_state">State</Label>
                      <Input
                        id="company_state"
                        value={invoiceSettings.company_state}
                        onChange={(e) => handleInvoiceSettingsChange('company_state', e.target.value)}
                        placeholder="TX"
                        className="rounded-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company_zip">ZIP</Label>
                      <Input
                        id="company_zip"
                        value={invoiceSettings.company_zip}
                        onChange={(e) => handleInvoiceSettingsChange('company_zip', e.target.value)}
                        placeholder="78701"
                        className="rounded-sm mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="company_phone">Phone</Label>
                    <Input
                      id="company_phone"
                      value={invoiceSettings.company_phone}
                      onChange={(e) => handleInvoiceSettingsChange('company_phone', e.target.value)}
                      placeholder="(512) 555-1234"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="default_tax_rate">Default Tax Rate (%)</Label>
                    <Input
                      id="default_tax_rate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={invoiceSettings.default_tax_rate}
                      onChange={(e) => handleInvoiceSettingsChange('default_tax_rate', parseFloat(e.target.value) || 0)}
                      placeholder="8.25"
                      className="rounded-sm mt-1"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Payment Links */}
              <div>
                <Label className="text-base font-medium flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Payment Links
                </Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Add your payment links - these will appear on your invoices so clients can pay online
                </p>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="stripe_payment_link">Stripe Payment Link</Label>
                    <Input
                      id="stripe_payment_link"
                      value={invoiceSettings.stripe_payment_link}
                      onChange={(e) => handleInvoiceSettingsChange('stripe_payment_link', e.target.value)}
                      placeholder="https://buy.stripe.com/..."
                      className="rounded-sm mt-1"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Create at: stripe.com → Products → Payment Links
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="venmo_username">Venmo Username</Label>
                    <Input
                      id="venmo_username"
                      value={invoiceSettings.venmo_username}
                      onChange={(e) => handleInvoiceSettingsChange('venmo_username', e.target.value)}
                      placeholder="@YourCompany"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="paypal_link">PayPal.me Link</Label>
                    <Input
                      id="paypal_link"
                      value={invoiceSettings.paypal_link}
                      onChange={(e) => handleInvoiceSettingsChange('paypal_link', e.target.value)}
                      placeholder="https://paypal.me/yourcompany"
                      className="rounded-sm mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="zelle_info">Zelle Email/Phone</Label>
                    <Input
                      id="zelle_info"
                      value={invoiceSettings.zelle_info}
                      onChange={(e) => handleInvoiceSettingsChange('zelle_info', e.target.value)}
                      placeholder="payments@yourcompany.com"
                      className="rounded-sm mt-1"
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* QR Code Option */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Include QR Code on Invoices</p>
                  <p className="text-sm text-muted-foreground">Adds a scannable QR code linking to your primary payment option</p>
                </div>
                <Switch
                  checked={invoiceSettings.include_qr_code}
                  onCheckedChange={(checked) => handleInvoiceSettingsChange('include_qr_code', checked)}
                />
              </div>

              {/* Email a copy to self */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email me a copy of invoices I send</p>
                  <p className="text-sm text-muted-foreground">BCCs your account email whenever you send or resend an invoice (you can override per-send)</p>
                </div>
                <Switch
                  checked={invoiceSettings.email_copy_to_self}
                  onCheckedChange={(checked) => handleInvoiceSettingsChange('email_copy_to_self', checked)}
                  data-testid="email-copy-to-self-switch"
                />
              </div>

              <Button 
                onClick={handleSaveInvoiceSettings} 
                disabled={savingInvoiceSettings}
                className="rounded-sm bg-slate-900 hover:bg-slate-800"
              >
                <Save className="h-4 w-4 mr-2" />
                {savingInvoiceSettings ? 'Saving...' : 'Save Invoice Settings'}
              </Button>
            </AccordionContent>
          </AccordionItem>

          {/* Lead Capture Form */}
          {leadFormData && (
            <AccordionItem value="lead-form" className="border rounded-sm px-4">
              <AccordionTrigger className="hover:no-underline" data-testid="settings-leadform-trigger">
                <span className="flex items-center gap-2 text-base font-semibold">
                  <FileText className="h-5 w-5" strokeWidth={1.5} />
                  Lead Capture Form
                </span>
              </AccordionTrigger>
              <AccordionContent className="space-y-6">
                {/* Direct Link */}
                <div>
                  <Label className="mb-2 block">Direct Link</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Share this URL with potential clients or link to it from your website
                  </p>
                  <div className="flex gap-2">
                    <Input
                      value={leadFormData.form_url || ''}
                      readOnly
                      className="rounded-sm font-mono text-sm"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(leadFormData.form_url, 'url')}
                      className="rounded-sm shrink-0"
                    >
                      {copiedUrl ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => window.open(leadFormData.form_url, '_blank')}
                      className="rounded-sm shrink-0"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Embed Code */}
                <div>
                  <Label className="mb-2 block">Embed Code</Label>
                  <p className="text-sm text-muted-foreground mb-2">
                    Copy this code and paste it into your website's HTML where you want the form to appear
                  </p>
                  <div className="relative">
                    <Textarea
                      value={leadFormData.embed_code || ''}
                      readOnly
                      className="rounded-sm font-mono text-xs h-24 resize-none"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(leadFormData.embed_code, 'embed')}
                      className="absolute top-2 right-2 rounded-sm"
                    >
                      {copiedEmbed ? <Check className="h-3 w-3 mr-1 text-green-600" /> : <Copy className="h-3 w-3 mr-1" />}
                      {copiedEmbed ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>

                <Separator />

                {/* Regenerate Token */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Regenerate Form URL</p>
                    <p className="text-xs text-muted-foreground">
                      Generate a new URL if you need to invalidate the old one
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerateToken}
                    disabled={regenerating}
                    className="rounded-sm"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
                    {regenerating ? 'Regenerating...' : 'Regenerate'}
                  </Button>
                </div>

                <div className="p-3 bg-orange-50 border border-orange-200 rounded-sm">
                  <p className="text-sm text-orange-800">
                    <strong>Tip:</strong> When someone fills out this form, a new lead will automatically appear in your Leads page with "Website Form" as the source.
                    {settingsData?.user?.google_connected && ' You\'ll also receive an email notification!'}
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          {/* Pipeline & Phases Info */}
          <AccordionItem value="pipeline" className="border rounded-sm px-4">
            <AccordionTrigger className="hover:no-underline" data-testid="settings-pipeline-trigger">
              <span className="flex items-center gap-2 text-base font-semibold">
                Sales Pipeline & Phases
              </span>
            </AccordionTrigger>
            <AccordionContent className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Sales Pipeline Stages</p>
                <div className="flex flex-wrap gap-2">
                  {['Lead', 'Qualified', 'Estimate', 'Proposal', 'Negotiation', 'Won', 'Lost'].map((stage, index) => (
                    <div key={stage} className="flex items-center gap-2 px-3 py-2 bg-secondary rounded-sm">
                      <span className="h-6 w-6 bg-slate-900 text-white rounded-sm flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="text-sm font-medium">{stage}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium mb-2">Construction Phases</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {['Design', 'Permit', 'Demo', 'Framing', 'Rough-ins', 'Drywall', 'Finishes', 'Final Inspection'].map((phase, index) => (
                    <div key={phase} className="flex items-center gap-2 px-3 py-2 bg-secondary rounded-sm">
                      <span className="h-5 w-5 bg-orange-500 text-white rounded-sm flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="text-xs font-medium">{phase}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          </Accordion>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Account Card */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg">Account</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="h-16 w-16 rounded-sm bg-slate-900 flex items-center justify-center text-white text-2xl font-bold">
                  {settingsData?.user?.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-semibold">{settingsData?.user?.name}</p>
                  <p className="text-sm text-muted-foreground">{settingsData?.user?.email}</p>
                </div>
              </div>
              <Separator />
              <div>
                <p className="text-xs text-muted-foreground">Company</p>
                <p className="text-sm">{settingsData?.organization?.name || settingsData?.user?.company || 'Not set'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Role</p>
                <p className="text-sm capitalize">{settingsData?.user?.role || 'Owner'}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Integrations</p>
                <div className="flex gap-2 mt-1">
                  {settingsData?.user?.google_connected && (
                    <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                      <Calendar className="h-3 w-3 mr-1" />
                      Google Connected
                    </Badge>
                  )}
                  {!settingsData?.user?.google_connected && (
                    <span className="text-xs text-muted-foreground">None connected</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* About */}
          <Card className="rounded-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="h-5 w-5 text-orange-500" strokeWidth={1.5} />
                Corbel CRM
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <p>
                Built for residential construction companies and remodelers in the United States.
              </p>
              <p>
                Track leads, manage projects, and keep your clients informed every step of the way.
              </p>
              <Separator className="my-4" />
              <div className="flex items-center justify-between">
                <p className="text-xs">Version {APP_VERSION}</p>
                {hasNewUpdate && (
                  <span className="flex items-center gap-1.5 text-xs text-orange-600 font-medium" data-testid="whats-new-badge">
                    <span className="h-2 w-2 rounded-full bg-orange-500 animate-pulse" />
                    Update available
                  </span>
                )}
              </div>
              <Link
                to="/whats-new"
                className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700 hover:underline mt-1"
                data-testid="whats-new-link"
              >
                <Sparkles className="h-3.5 w-3.5" />
                See what's new in Corbel
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
